import { AlertDialog, Button, Card, CardBody, CardHeader, EmptyState, Spinner } from '@bennows/boows-react';
import { KeyRound, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate, formatDateTime } from '../../lib/format';
import { useToast } from '../../lib/toasts';
import { passkeysSupported } from '../../lib/webauthn';
import { AddPasskeyDialog } from './AddPasskeyDialog';
import { usePasskeys, usePasskeyWrites, type Passkey } from './usePasskeys';

/** The own passkeys on "Mein Konto": list, add, remove (REQ-140-004, T460-1-4). */
export function PasskeysCard() {
  const { t } = useTranslation();
  const toast = useToast();
  const supported = passkeysSupported();
  const passkeys = usePasskeys({ enabled: supported });
  const { remove } = usePasskeyWrites();
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState<Passkey | null>(null);

  const confirmRemove = () => {
    if (!removing) return;
    const passkey = removing;
    setRemoving(null);
    remove.mutate(passkey.id, {
      onSuccess: () => toast(t('passkeys.removed', { name: passkey.name })),
      onError: () => toast(t('common.errorUnexpected'), 'error'),
    });
  };

  const body = () => {
    if (!supported) return <p className="ui-console-muted">{t('passkeys.unsupported')}</p>;
    if (passkeys.isPending) return <Spinner label={t('common.loading')} />;
    if (passkeys.isError) {
      return (
        <div className="ui-error-state" role="alert">
          <p className="ui-error-state__text">{t('passkeys.loadError')}</p>
          <Button variant="secondary" onClick={() => void passkeys.refetch()}>
            {t('common.retry')}
          </Button>
        </div>
      );
    }
    if (passkeys.data.items.length === 0) return <EmptyState icon={<KeyRound aria-hidden="true" />} title={t('passkeys.empty')} />;
    return (
      <ul className="ui-console-passkeys" aria-label={t('passkeys.listLabel')}>
        {passkeys.data.items.map((passkey) => (
          <li key={passkey.id} className="ui-console-passkey">
            <KeyRound className="ui-console-passkey__icon" aria-hidden="true" />
            <span className="ui-console-stacked">
              <strong>{passkey.name}</strong>
              <span className="ui-console-muted">
                {t('passkeys.created', { date: formatDate(passkey.created_at) })}
                {' · '}
                {passkey.last_used_at ? t('passkeys.lastUsed', { date: formatDateTime(passkey.last_used_at) }) : t('passkeys.neverUsed')}
              </span>
            </span>
            <Button variant="ghost" size="sm" iconOnly aria-label={t('passkeys.removeLabel', { name: passkey.name })} title={t('passkeys.remove')} onClick={() => setRemoving(passkey)}>
              <Trash2 aria-hidden="true" />
            </Button>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Card as="section" aria-labelledby="account-passkeys">
      <CardHeader
        title={<span id="account-passkeys">{t('passkeys.title')}</span>}
        action={
          supported ? (
            <Button variant="secondary" size="sm" iconPrefix={<Plus aria-hidden="true" />} onClick={() => setAdding(true)}>
              {t('passkeys.add')}
            </Button>
          ) : undefined
        }
      />
      <CardBody>
        <div className="ui-stack">
          <p className="ui-console-muted">{t('passkeys.lead')}</p>
          {body()}
        </div>
      </CardBody>
      {adding ? <AddPasskeyDialog onClose={() => setAdding(false)} /> : null}
      <AlertDialog
        open={removing !== null}
        tone="danger"
        title={removing ? t('passkeys.confirmRemove.title', { name: removing.name }) : ''}
        description={t('passkeys.confirmRemove.text')}
        confirmLabel={t('passkeys.confirmRemove.action')}
        cancelLabel={t('common.cancel')}
        onCancel={() => setRemoving(null)}
        onConfirm={confirmRemove}
      />
    </Card>
  );
}
